import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Globals } from '../globalfunctions.js';
import styles from './Settings.module.css';
import TwoFactorAuth from './TwoFactorAuth';
import PasswordStrengthIndicator from './PasswordStrengthIndicator';
import roleManager, { PERMISSIONS } from '../utils/roleManager';
import mfaEnforcer from '../utils/mfaEnforcer';
import PageShell from './ui/PageShell';
import Button from './ui/Button';
import FormField from './ui/FormField';
import { apiClient, ApiError } from '../utils/apiClient';

const PREFERENCES_STORAGE_KEY = 'userPreferences';

const loadStoredPreferences = () => {
    try {
        const stored = JSON.parse(localStorage.getItem(PREFERENCES_STORAGE_KEY));
        return {
            emailNotifications: stored?.emailNotifications ?? true,
            marketingNotifications: stored?.marketingNotifications ?? false,
            defaultCurrency: stored?.defaultCurrency ?? 'GBP',
        };
    } catch {
        return { emailNotifications: true, marketingNotifications: false, defaultCurrency: 'GBP' };
    }
};

function Settings() {
    const navigate = useNavigate();
    const [show2FAReconfigure, setShow2FAReconfigure] = useState(false);
    const [twoFactorStatus, setTwoFactorStatus] = useState({ enabled: false, method: '', timeRemaining: '' });
    const [showChangePassword, setShowChangePassword] = useState(false);
    const [passwordData, setPasswordData] = useState({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
    });
    const [passwordStrength, setPasswordStrength] = useState(null);
    const [message, setMessage] = useState({ type: '', text: '' });
    const [preferences, setPreferences] = useState(loadStoredPreferences);
    const [signature, setSignature] = useState('');
    const [savingSignature, setSavingSignature] = useState(false);
    const [previewHtml, setPreviewHtml] = useState('');

    useEffect(() => {
        if (Globals.LoginTime === null) {
            navigate('/login');
            return;
        }

        // Check 2FA status
        const method = localStorage.getItem('2faEnabled');
        const setupComplete = mfaEnforcer.is2FASetupComplete();
        const sessionValid = mfaEnforcer.is2FASessionValid();
        const timeRemaining = mfaEnforcer.formatTimeRemaining(mfaEnforcer.getSessionTimeRemaining());

        setTwoFactorStatus({
            enabled: setupComplete,
            method: method || 'Not configured',
            sessionValid: sessionValid,
            timeRemaining: timeRemaining
        });

        apiClient.get('/api/users/me')
            .then((data) => setSignature(data.signature || ''))
            .catch(() => {});
    }, [navigate]);

    // Live preview: renders the real email template (see
    // ticketMailer.service.js buildTicketEmailHtml) server-side with
    // whatever's currently typed, not necessarily saved yet - debounced
    // so it's not firing a request on every keystroke.
    useEffect(() => {
        const timeout = setTimeout(() => {
            apiClient.post('/api/users/me/signature-preview', { signature })
                .then((data) => setPreviewHtml(data.html || ''))
                .catch(() => {});
        }, 400);
        return () => clearTimeout(timeout);
    }, [signature]);

    const handle2FAReconfigure = () => {
        setShow2FAReconfigure(true);
    };

    const handle2FASuccess = (method) => {
        // Update 2FA status after reconfiguration
        const setupComplete = mfaEnforcer.is2FASetupComplete();
        const sessionValid = mfaEnforcer.is2FASessionValid();
        const timeRemaining = mfaEnforcer.formatTimeRemaining(mfaEnforcer.getSessionTimeRemaining());

        setTwoFactorStatus({
            enabled: setupComplete,
            method: method,
            sessionValid: sessionValid,
            timeRemaining: timeRemaining
        });

        setShow2FAReconfigure(false);
        setMessage({ type: 'success', text: `Two-factor authentication reconfigured using ${method}` });
        setTimeout(() => setMessage({ type: '', text: '' }), 5000);
    };

    const handle2FACancel = () => {
        setShow2FAReconfigure(false);
    };

    const handlePasswordChange = async (e) => {
        e.preventDefault();

        if (passwordData.newPassword !== passwordData.confirmPassword) {
            setMessage({ type: 'error', text: 'New passwords do not match' });
            return;
        }

        if (!passwordStrength || passwordStrength.level === 'weak') {
            setMessage({ type: 'error', text: 'New password is too weak' });
            return;
        }

        try {
            await apiClient.post('/api/users/change-password', {
                currentPassword: passwordData.currentPassword,
                newPassword: passwordData.newPassword
            });
            setMessage({ type: 'success', text: 'Password changed successfully' });
            setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
            setShowChangePassword(false);
        } catch (error) {
            setMessage({ type: 'error', text: error instanceof ApiError ? error.message : 'Error changing password' });
        }
        setTimeout(() => setMessage({ type: '', text: '' }), 5000);
    };

    const saveSignature = async (e) => {
        e.preventDefault();
        setSavingSignature(true);
        try {
            await apiClient.put('/api/users/me/signature', { signature });
            setMessage({ type: 'success', text: 'Signature saved' });
        } catch (error) {
            setMessage({ type: 'error', text: 'Error saving signature' });
        } finally {
            setSavingSignature(false);
            setTimeout(() => setMessage({ type: '', text: '' }), 5000);
        }
    };

    // Notification/currency preferences have no backend column or endpoint
    // (see japan DB schema audit - users table carries no such fields), so
    // these are stored client-side. If they need to sync across devices
    // later, this is the one place that would need to change to call an API.
    const handleSaveSettings = (e) => {
        e.preventDefault();
        localStorage.setItem(PREFERENCES_STORAGE_KEY, JSON.stringify(preferences));
        setMessage({ type: 'success', text: 'Settings saved' });
        setTimeout(() => setMessage({ type: '', text: '' }), 5000);
    };

    return (
        <PageShell>
            <div className={styles.settingsPage}>
                <h1>Settings</h1>

                {message.text && (
                    <div className={`${styles.message} ${message.type === 'success' ? styles.messageSuccess : styles.messageError}`}>
                        {message.text}
                    </div>
                )}

                <h2 className={styles.sectionTitle}>Security</h2>
                <div className={styles.card}>
                    <FormField label="Two-Factor Authentication (Mandatory)">
                        <div className={styles.twoFactorRow}>
                            <span className={`${styles.badge} ${twoFactorStatus.enabled ? styles.badgeSuccess : styles.badgeWarning}`}>
                                {twoFactorStatus.enabled ? 'Configured' : 'Not Configured'}
                            </span>
                            <span className={styles.mutedText}>Method: <strong>{twoFactorStatus.method.toUpperCase()}</strong></span>
                            {twoFactorStatus.enabled && (
                                <Button type="button" variant="secondary" onClick={handle2FAReconfigure}>Reconfigure</Button>
                            )}
                        </div>
                        {twoFactorStatus.sessionValid && (
                            <p className={styles.infoNote}><i className="fas fa-lock"></i> Current session verified - expires in {twoFactorStatus.timeRemaining}</p>
                        )}
                        <p className={styles.warningNote}>
                            <strong>Security Policy:</strong> Two-factor authentication is mandatory for all users and cannot be disabled. This ensures maximum account security.
                        </p>
                    </FormField>

                    <FormField label="Password">
                        <Button type="button" variant="secondary" onClick={() => setShowChangePassword(!showChangePassword)}>
                            {showChangePassword ? 'Cancel' : 'Change Password'}
                        </Button>
                    </FormField>

                    {showChangePassword && (
                        <form onSubmit={handlePasswordChange} className={styles.nestedForm}>
                            <FormField id="currentPassword" label="Current Password">
                                <input
                                    id="currentPassword"
                                    type="password"
                                    value={passwordData.currentPassword}
                                    onChange={(e) => setPasswordData({ ...passwordData, currentPassword: e.target.value })}
                                    placeholder="Enter current password"
                                />
                            </FormField>
                            <FormField id="newPassword" label="New Password">
                                <input
                                    id="newPassword"
                                    type="password"
                                    value={passwordData.newPassword}
                                    onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                                    placeholder="Enter new password"
                                />
                                <PasswordStrengthIndicator
                                    password={passwordData.newPassword}
                                    onStrengthChange={setPasswordStrength}
                                />
                            </FormField>
                            <FormField id="confirmPassword" label="Confirm New Password">
                                <input
                                    id="confirmPassword"
                                    type="password"
                                    value={passwordData.confirmPassword}
                                    onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                                    placeholder="Confirm new password"
                                />
                            </FormField>
                            <Button type="submit">Update Password</Button>
                        </form>
                    )}
                </div>

                <h2 className={styles.sectionTitle}>Profile</h2>
                <div className={styles.card}>
                    <div className={styles.grid}>
                        <FormField label="Name">
                            <input type="text" value={`${Globals.FirstName} ${Globals.LastName}`} readOnly />
                        </FormField>
                        <FormField label="Username/Email">
                            <input type="text" value={Globals.UserName} readOnly />
                        </FormField>
                        <FormField label="Role">
                            <input type="text" value={roleManager.getCurrentUserRole()} readOnly />
                        </FormField>
                    </div>
                </div>

                <h2 className={styles.sectionTitle}>Email Signature</h2>
                <p className={styles.sectionIntro}>
                    Appended to the bottom of your own ticket replies and forwards, in place of the generic sign-off.
                    Plain text is fine, or use your own HTML (e.g. <code>&lt;strong&gt;</code>, <code>&lt;a href="..."&gt;</code>,
                    an <code>&lt;img&gt;</code> logo) for a richer signature - typed line breaks are preserved either way.
                </p>
                <div className={styles.signatureLayout}>
                    <div className={styles.card}>
                        <form onSubmit={saveSignature}>
                            <FormField id="signature" label="Signature">
                                <textarea
                                    id="signature"
                                    rows={8}
                                    value={signature}
                                    onChange={(e) => setSignature(e.target.value)}
                                    placeholder={`${Globals.FirstName} ${Globals.LastName}\nVader Design Support`}
                                />
                            </FormField>
                            <Button type="submit" disabled={savingSignature}>{savingSignature ? 'Saving...' : 'Save Signature'}</Button>
                        </form>
                    </div>
                    <div className={styles.previewCard}>
                        <div className={styles.previewLabel}>Live preview - how a reply will look</div>
                        <iframe
                            key="signature-preview"
                            className={styles.previewFrame}
                            srcDoc={previewHtml}
                            title="Signature preview"
                        />
                    </div>
                </div>

                <h2 className={styles.sectionTitle}>Notification Preferences</h2>
                <div className={styles.card}>
                    <form onSubmit={handleSaveSettings}>
                        <div className={styles.checkboxRow}>
                            <input
                                type="checkbox"
                                id="emailNotif"
                                checked={preferences.emailNotifications}
                                onChange={(e) => setPreferences({ ...preferences, emailNotifications: e.target.checked })}
                            />
                            <label htmlFor="emailNotif">Email Notifications</label>
                        </div>
                        <div className={styles.checkboxRow}>
                            <input
                                type="checkbox"
                                id="smsNotif"
                                checked={preferences.marketingNotifications}
                                onChange={(e) => setPreferences({ ...preferences, marketingNotifications: e.target.checked })}
                            />
                            <label htmlFor="smsNotif">Marketing Notifications</label>
                        </div>

                        {roleManager.hasPermission(PERMISSIONS.MANAGE_SETTINGS) && (
                            <>
                                <h3 className={styles.subsectionTitle}>Invoice Settings</h3>
                                <FormField id="defaultCurrency" label="Default Currency">
                                    <select
                                        id="defaultCurrency"
                                        value={preferences.defaultCurrency}
                                        onChange={(e) => setPreferences({ ...preferences, defaultCurrency: e.target.value })}
                                    >
                                        <option>GBP</option>
                                        <option>USD</option>
                                        <option>EUR</option>
                                    </select>
                                </FormField>
                            </>
                        )}

                        <Button type="submit">Save Preferences</Button>
                    </form>
                </div>
            </div>
            {show2FAReconfigure && (
                <TwoFactorAuth
                    mode="setup"
                    onSuccess={handle2FASuccess}
                    onCancel={handle2FACancel}
                />
            )}
        </PageShell>
    );
}

export default Settings;
